import type { ThemeColors, ThemeConfig } from "./types";
import { THEME_COLOR_KEYS } from "./types";

// ID for the dynamic theme style element
const THEME_STYLE_ID = "vizi-theme-editor-styles";

// Current radius value for inclusion in style element
let currentRadius = "0.5rem";

/**
 * Build the complete CSS for theme overrides
 */
function buildThemeCSS(colors: ThemeColors): string {
  // Build CSS string with all color variables
  // Use !important to override Tailwind v4's @theme inline generated CSS
  let cssVars = "";
  THEME_COLOR_KEYS.forEach((key) => {
    const value = colors[key];
    if (value) {
      // Set both base variable and Tailwind color variable with !important
      cssVars += `  --${key}: ${value} !important;\n`;
      cssVars += `  --color-${key}: hsl(${value}) !important;\n`;
    }
  });

  // Add radius variable
  cssVars += `  --radius: ${currentRadius} !important;\n`;

  // Use simple :root selector - CSS custom properties inherit globally
  return `:root {\n${cssVars}}`;
}

/**
 * Get or create the theme style element
 */
function getThemeStyleElement(): HTMLStyleElement {
  let styleEl = document.getElementById(
    THEME_STYLE_ID,
  ) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = THEME_STYLE_ID;
    document.head.appendChild(styleEl);
  }
  return styleEl;
}

/**
 * Apply theme colors to document CSS variables
 * Immediately updates the UI without page reload
 *
 * Uses a dynamic style element to override Tailwind v4's @theme inline
 * which may be processed at build time.
 */
export function applyThemeColors(colors: ThemeColors): void {
  const styleEl = getThemeStyleElement();
  styleEl.textContent = buildThemeCSS(colors);

  // Also set radius directly on documentElement for immediate effect
  document.documentElement.style.setProperty("--radius", currentRadius);
}

/**
 * Apply border radius to document
 * Updates the currentRadius and rebuilds theme CSS with !important
 */
export function applyRadius(radius: string, colors?: ThemeColors): void {
  currentRadius = radius;
  // If colors provided, rebuild the full CSS; otherwise just set the property
  if (colors) {
    const styleEl = getThemeStyleElement();
    styleEl.textContent = buildThemeCSS(colors);
  } else {
    // Fallback: directly set the property (may be overridden by existing style element)
    document.documentElement.style.setProperty("--radius", radius);
  }
}

/**
 * Apply a complete theme (colors + radius) based on current mode
 */
export function applyTheme(theme: ThemeConfig, isDark: boolean): void {
  const colors = isDark ? theme.dark : theme.light;
  currentRadius = theme.radius;
  applyThemeColors(colors);
}

/**
 * Get current theme colors from CSS variables
 */
export function getCurrentThemeColors(): ThemeColors {
  const root = document.documentElement;
  const style = getComputedStyle(root);

  const colors: Partial<ThemeColors> = {};
  THEME_COLOR_KEYS.forEach((key) => {
    colors[key] = style.getPropertyValue(`--${key}`).trim();
  });

  return colors as ThemeColors;
}

/**
 * Convert HSL string (e.g., "262 83% 58%") to hex color
 */
export function hslToHex(hsl: string): string {
  const parts = hsl.split(" ");
  if (parts.length < 3) return "#000000";

  const h = parseFloat(parts[0]) || 0;
  const s = (parseFloat(parts[1]) || 0) / 100;
  const l = (parseFloat(parts[2]) || 0) / 100;

  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };

  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Convert hex color to HSL string (e.g., "#8b5cf6" -> "262 83% 58%")
 */
export function hexToHsl(hex: string): string {
  // Remove # if present
  hex = hex.replace(/^#/, "");

  // Parse hex values
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
        break;
      case g:
        h = ((b - r) / d + 2) * 60;
        break;
      case b:
        h = ((r - g) / d + 4) * 60;
        break;
    }
  }

  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Export theme as JSON string
 */
export function exportThemeToJson(theme: ThemeConfig): string {
  return JSON.stringify(theme, null, 2);
}

/**
 * Download theme as JSON file
 */
export function downloadTheme(theme: ThemeConfig): void {
  const json = exportThemeToJson(theme);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = `${theme.name}-theme.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Load theme from a File object
 */
export async function loadThemeFromFile(file: File): Promise<ThemeConfig> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const theme = JSON.parse(e.target?.result as string) as ThemeConfig;

        // Basic validation
        if (!theme.name || !theme.light || !theme.dark) {
          reject(new Error("Invalid theme file: missing required fields"));
          return;
        }

        resolve(theme);
      } catch {
        reject(new Error("Invalid JSON file"));
      }
    };

    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file);
  });
}

/**
 * Format color key for display (e.g., "card-foreground" -> "Card Foreground")
 */
export function formatColorLabel(key: string): string {
  return key
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
